import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Container, Sprite } from '@pixi/react';
import * as PIXI from 'pixi.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CARD_NON_RANKED_ANIMATION_SET, CARD_RANKED_ANIMATION_SET, CARD_SHOW_ANIMATION_SET } from '../../../animations/animationDefinitions';
import { getCardShorthand } from '../../../types/cardTypes';
import { CARD_ASSET_URLS } from '../../../utils/loaderUtils';
import { HOLE_CARDS_OUTLINE_FILTER } from '../../../utils/pixiFilterUtils';
import { isTestEnvironment } from '../../../utils/utils';
import { AnimationContainer } from '../pixiComponents/animationContainer';
import { DEFAULT_CARD_TEXTURE_SIZE_PX } from './cardGroupUtils';
import { LayeredSpriteCardDisplay } from './layeredSpriteCardDisplay';
const HIT_AREA_WIDTH_RATIO = 700 / 1024;
const HIT_AREA_HEIGHT_RATIO = 980 / 1024;
const DEFAULT_ANGLE = 5;
function getContainerFilters(isHovering) {
    return isHovering ? [HOLE_CARDS_OUTLINE_FILTER] : null;
}
const ANIMATION_SETS = {
    show: CARD_SHOW_ANIMATION_SET,
    ranked: CARD_RANKED_ANIMATION_SET,
    'non-ranked': CARD_NON_RANKED_ANIMATION_SET
};
export const CardDisplay = ({ cardRankChar, cardSuitChar, cardIndex, cardEmphasis, cardRenderProperties, pixiDataType, isFourColorDeck, seatIndex, startTimestampMs, animationType, onClick, isPendingDiscard, dropShadowAlpha }) => {
    const [isHovering, setIsHovering] = useState(false);
    const containerRef = useRef(null);
    const cardContainerRef = useRef(null);
    const hasOnClick = !!onClick;
    const hitArea = useMemo(() => {
        const hitAreaWidthPx = DEFAULT_CARD_TEXTURE_SIZE_PX * HIT_AREA_WIDTH_RATIO;
        const hitAreaHeightPx = DEFAULT_CARD_TEXTURE_SIZE_PX * HIT_AREA_HEIGHT_RATIO;
        return new PIXI.Rectangle(-hitAreaWidthPx / 2, -hitAreaHeightPx / 2, hitAreaWidthPx, hitAreaHeightPx);
    }, []);
    const onMouseOver = useCallback(() => {
        setIsHovering(true);
    }, []);
    const onMouseOut = useCallback(() => {
        setIsHovering(false);
    }, []);
    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.filters = getContainerFilters(isHovering);
        }
    }, [isHovering]);
    /**
     * When existence of onClick is changing, it often means we are entering the discard phase.
     * We could have stale `isHovering` state from the previous hand for this card index's component, so make sure it is
     * reset so the discard phase does not start with the previous hand's discard card highlighted from a stale mouse hover.
     */
    useEffect(() => {
        setIsHovering(false);
    }, [hasOnClick]);
    const { x, y, scale, angle } = cardRenderProperties;
    const containerProps = {
        x,
        y,
        scale,
        angle: angle !== undefined ? angle : DEFAULT_ANGLE,
        ...(onClick
            ? {
                ref: containerRef,
                filters: getContainerFilters(isHovering),
                cursor: 'pointer',
                interactive: true,
                pointerdown: onClick,
                accessible: true,
                mouseover: onMouseOver,
                mouseout: onMouseOut,
                hitArea
            }
            : {}),
        'pixi-data': isTestEnvironment()
            ? {
                type: pixiDataType,
                isFrontShown: !!(cardRankChar && cardSuitChar),
                cardIndex,
                seatIndex,
                cardEmphasis,
                isFourColorDeck,
                cardShorthand: cardRankChar && cardSuitChar ? getCardShorthand(cardRankChar, cardSuitChar) : undefined
            }
            : null
    };
    const cardPartX = -Math.round(DEFAULT_CARD_TEXTURE_SIZE_PX / 2);
    const cardPartY = -Math.round(DEFAULT_CARD_TEXTURE_SIZE_PX / 2);
    const cardSpriteElement = (_jsx(LayeredSpriteCardDisplay, { cardRankChar: cardRankChar, cardSuitChar: cardSuitChar, cardEmphasis: cardEmphasis, x: cardPartX, y: cardPartY, isFourColorDeck: isFourColorDeck, isPendingDiscard: isPendingDiscard, dropShadowAlpha: dropShadowAlpha }));
    const cardSpriteContainer = cardRankChar !== undefined && startTimestampMs !== undefined && animationType ? (_jsx(AnimationContainer, { startTimestampMs: startTimestampMs, animationSet: ANIMATION_SETS[animationType], children: cardSpriteElement })) : (cardSpriteElement);
    return (_jsxs(Container, { ...containerProps, children: [_jsx(Container, { ref: cardContainerRef, children: cardSpriteContainer }), isPendingDiscard && (_jsx(Sprite, { "pixi-data": isTestEnvironment() ? { type: 'pending-discard-card', cardIndex } : null, texture: PIXI.Texture.from(CARD_ASSET_URLS['discard-overlay']), x: -DEFAULT_CARD_TEXTURE_SIZE_PX / 2, y: -DEFAULT_CARD_TEXTURE_SIZE_PX / 2, alpha: 0.65 }))] }));
};
